import { defineStore, acceptHMRUpdate, storeToRefs } from "pinia";
import Api from "../utils/api";
import Helper from "../utils/helper";
import { useFormTemplateResponseStore } from "./formTemplateResponseStore";

export const useFormPageStore = defineStore("form_page_store", {
  persist: true,
  state: () => ({
    formPageList: {}, // Use an object to store data with keys as IDs,
    formProgress: {} // tracks the current progress
  }),
  actions: {
    async getFormPageById(id, fetchWithFields, offlineFirst = true) {
      return await Api.FormPages.getFormPageById(
        id,
        fetchWithFields,
        offlineFirst
      );
    },
    // Returns the list without storing it — formPageList (persisted) is keyed
    // pages the app is using, not the whole library.
    async fetchFormPagesList(config) {
      return await Api.FormPages.getFormPagesList(config);
    },
    async updateFormPage(data) {
      const _res = await Api.FormPages.updateFormPage(data);
      if (_res.data) {
        // Update the page in the local state
        // Since we're updating by ID, we only need the first item if it's an array
        const updatedData = Array.isArray(_res.data) ? _res.data[0] : _res.data;
        this.formPageList[data.id] = {
          ...this.formPageList[data.id],
          ...updatedData
        };
      }
      return _res;
    },
    async updateFormPageFromList(
      data,
      updateProgress = true,
      routeParams = null,
      offlineFirst = true
    ) {
      const formTemplateResponseStore = useFormTemplateResponseStore();
      let { response } = storeToRefs(formTemplateResponseStore);

      data = Helper.deepCopy(data);
      if (routeParams && routeParams.res) {
        let _response =
          await useFormTemplateResponseStore().getFormTemplateResponseById(
            routeParams.res,
            false, // can pass here true if need to use local data,
            offlineFirst
          );
        if (_response.error) throw _response.error;
        _response = _response.data;
        // get the response data for the page
        let resData = Helper.findAndSetKeyInObject(_response.response, data.id);
        if (Helper.isValueExist(resData)) {
          if (data && data.pageObj && data.pageObj.fields) {
            data.pageObj.fields = Helper.processFieldValues(
              data.pageObj.fields,
              resData
            );
          }
        }
      }
      let _processedPage = [];
      // Update the object where key is the ID at the page level
      this.formPageList[data.id] = { ...this.formPageList[data.id], ...data };
      // Recursive function to update nested list items
      const updateNestedItems = (items) => {
        for (const item of items) {
          if (item.id === data.id) {
            Object.assign(item, data);
          }
          if (item.list_items && Array.isArray(item.list_items)) {
            updateNestedItems(item.list_items);
          }
        }
      };

      // Iterate through the formPageList to find and update the corresponding item in list_items
      for (const page of Object.values(this.formPageList)) {
        _processedPage.push(page.id);
        if (page.is_list && Array.isArray(page.list_items)) {
          updateNestedItems(page.list_items);
        }
      }
      if (updateProgress) {
        let _existingPage = Helper.deepCopy(this.formPageList[data.id]);
        // current page data
        const _currentFormData = Helper.findAndSetKeyInObject(
          response.value,
          data.id
        );
        // pulling row data as json
        const _data = Helper.generateFormTemplateResponse(
          {
            id: null,
            pages: [_existingPage]
          },
          false,
          Helper.deepCopy(_currentFormData)
        );
        // now replace
        let _progress = Helper.calculateFilledPercentage(_data)[data.id];
        if (_progress) {
          this.updateFormProgress({
            [data.id]: _progress
          });
        }
      }
      // returning the updated data with value
      if (routeParams && routeParams.res) {
        return data;
      }
    },
    async updateFormProgress(progressData) {
      if (progressData) {
        this.formProgress = {
          ...this.formProgress,
          ...progressData
        };
      }
    },
    getFormProgress(formId, sumOf = null) {
      try {
        if (sumOf && sumOf.length >= 0) {
          // Calculate the average progress for the given array of IDs
          const totalProgress = sumOf.reduce(
            (sum, id) => sum + (this.formProgress[id] || 0),
            0
          );
          const itemCount = sumOf.length;
          itemCount > 0 ? Math.round(totalProgress / itemCount) : 0;
          this.formProgress[formId] =
            itemCount > 0 ? Math.round(totalProgress / itemCount) : 0;
          return Math.round(this.formProgress[formId] || 0);
        } else {
          // Get progress for a single ID
          return Math.round(this.formProgress[formId] || 0);
        }
      } catch {
        return 0;
      }
    },

    async getFormPageFromList(id) {
      // Update the object where key is the ID
      return this.formPageList[id] || null;
    },
    async resetPageFromList() {
      this.$reset();
    }
  },
  $reset() {
    this.formPageList = {}; // Reset formPageList to an empty object
    this.formProgress = {};
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFormPageStore, import.meta.hot));
}
